import {
    PaymentAuthorisationType,
    WalletPaymentErrorType,
    WalletPaymentSubmissionFailure,
    WalletPaymentSubmissionResult,
    WalletProvider
} from '../payment';
import {Signal, computed, signal} from "@angular/core";
import {PaymentModel} from "../payment.model";
import {ConfigurationModel} from "../../configuration/configuration.model";
import {WalletPaymentProcessor} from "./wallet-payment-processor";
import {AdyenApplePaymentData, AdyenGooglePaymentData} from "../adyen";

export class AdyenWalletPaymentProcessor extends WalletPaymentProcessor {
    public readonly quickPayAvailable: Signal<boolean> = computed<boolean>(() => this.walletProvider() !== undefined);
    public readonly canSubmit: Signal<boolean> = signal<boolean>(true).asReadonly();

    private adyenCheckout: any;
    private adyenGooglePay: any;
    private adyenApplePay: any;
    private submissionResult: WalletPaymentSubmissionResult = new WalletPaymentSubmissionResult(PaymentAuthorisationType.ADYEN_PAYMENT_METHOD);

    public paymentMethodsResponse: any;

    constructor(paymentModel: PaymentModel, configurationModel: ConfigurationModel) {
        super(paymentModel, configurationModel);
        this.paymentMethodsResponse = JSON.parse(this.paymentModel.paymentMethods());
    }

    public async init(): Promise<WalletPaymentProcessor> {
        if (!this.paymentModel.paymentPlatformPublicKey()) {
            return undefined;
        }

        // noinspection JSUnresolvedReference
        try {
            this.adyenCheckout = await (<any>window).AdyenWeb.AdyenCheckout({
                clientKey: this.paymentModel.paymentPlatformPublicKey(),
                environment: this.paymentModel.paymentPlatformLiveMode() ? "live-au" : "test",
                locale: "en_US",
                countryCode: "AU",
                paymentMethodsResponse: this.paymentMethodsResponse
            });

            this.buildApplePayComponent();
            this.buildGooglePayComponent();
        } catch (error) {
            console.error("Adyen wallet payment initialisation failed", error);
            return undefined;
        }

        await this.resolveWalletProvider();
        if (this.walletProvider() == undefined) {
            return undefined;
        }
        return this;
    }

    // A missing component (adyenApplePay/adyenGooglePay both undefined) means the platform was ruled out
    // synchronously at construction time (e.g. buildApplePayComponent()'s iOS check) - definitely not
    // available (undefined). A component that exists but rejects isAvailable() (existingPaymentMethodRequired
    // means this also covers "no card provisioned yet") is "not sure yet" (null) and worth retrying.
    protected async determineWalletProvider(): Promise<WalletProvider | null | undefined> {
        if (this.adyenApplePay) {
            try {
                await this.adyenApplePay.isAvailable();
                return WalletProvider.APPLE_PAY;
            } catch (error) {
                console.log("Adyen apple pay not available", error);
            }
        }
        if (this.adyenGooglePay) {
            try {
                await this.adyenGooglePay.isAvailable();
                return WalletProvider.GOOGLE_PAY;
            } catch (error) {
                console.log("Adyen google pay not available", error);
            }
        }
        return this.adyenApplePay || this.adyenGooglePay ? null : undefined;
    }

    private buildApplePayComponent(amount: number = 1): void {
        if (!this.paymentModel.responsiveService.isIos()) {
            return;
        }

        const applePayMethod: any = this.paymentMethodsResponse.paymentMethods.find((paymentMethod: any) => paymentMethod.name === "Apple Pay");
        const applePayConfiguration = applePayMethod && applePayMethod.configuration ? JSON.parse(JSON.stringify(applePayMethod.configuration)) : {};
        if (this.paymentModel.paymentPlatformLiveMode() || !applePayConfiguration.merchantName) {
            applePayConfiguration.merchantName = this.configurationModel.app()?.googlePayMerchantName;
        }
        // applePayMethod.brands = ["amex", "maestro", "mc", "visa"];

        // noinspection JSUnusedGlobalSymbols,JSUnusedLocalSymbols
        this.adyenApplePay = new (<any>window).AdyenWeb.ApplePay(this.adyenCheckout, {
            configuration: applePayConfiguration,
            existingPaymentMethodRequired: true,
            amount: {
                value: Math.round(amount * 100),
                currency: "AUD"
            },
            countryCode: "AU",
            totalPriceLabel: "Total",
            requiredShippingContactFields: [
                ...this.paymentModel.requestPayerEmail() ? ["email"] : [],
                ...this.paymentModel.requestPayerPhone() ? ["phone"] : [],
                ...this.paymentModel.requestPayerName() ? ["name"] : [],
            ],
            requiredBillingContactFields: [
                // ...this.paymentModel.requestPayerPostcode() ? ["postalAddress"] : []
            ],
            onAuthorized: (
                paymentData: AdyenApplePaymentData,
                actions: {
                    resolve: () => void,
                    reject: () => void
                }
            ) => {
                if (paymentData.authorizedEvent) {
                    if (paymentData.authorizedEvent.payment && paymentData.authorizedEvent.payment.shippingContact) {
                        this.submissionResult.payerName = (paymentData.authorizedEvent.payment.shippingContact.givenName + " " + paymentData.authorizedEvent.payment.shippingContact.familyName).trim();
                        this.submissionResult.payerPhone = paymentData.authorizedEvent.payment.shippingContact.phoneNumber;
                        this.submissionResult.payerEmail = paymentData.authorizedEvent.payment.shippingContact.emailAddress;
                    }
                    if (paymentData.billingAddress) {
                        // this.submissionResult.payerPostcode = paymentData.billingAddress.postalCode;
                    }
                }
                actions.resolve();
            },
            onSubmit: (
                state: {
                    data: any,
                    isValid: boolean
                },
                elementRef: any,
                actions: {
                    resolve: (resultCode: string) => void,
                    reject: () => void
                }
            ) => {
                if (state.isValid) {
                    actions.resolve("Received");
                    this.submissionResult.token = btoa(JSON.stringify(state.data));
                    void this.paymentModel.analyticsService.trackWalletPaymentRequestSucceeded(this.walletProvider());
                    this.resolveSubmission(this.submissionResult);
                } else {
                    actions.reject();
                }
            },
            onError: (
                error: {
                    name: string,
                    message: string,
                    cause: string,
                    sourceURL: string,
                    stack: string
                },
                elementRef: any
            ) => {
                void this.paymentModel.analyticsService.trackWalletPaymentRequestCancelled(this.walletProvider());
                if (this.rejectSubmission) {
                    this.rejectSubmission(new WalletPaymentSubmissionFailure(error.name === "CANCEL" ? WalletPaymentErrorType.APPLE_PAY_CANCELLED : WalletPaymentErrorType.PROVIDER_ERROR,
                        error.name, typeof error.cause === "string" ? error.cause : error.message, typeof error.cause === "string" ? error.message : undefined));
                }
            }
        });
    }

    private buildGooglePayComponent(amount: number = 1): void {
        const googlePayMethod: any = this.paymentMethodsResponse.paymentMethods.find((paymentMethod: any) => paymentMethod.name === "Google Pay");
        const googlePayConfiguration = googlePayMethod && googlePayMethod.configuration ? JSON.parse(JSON.stringify(googlePayMethod.configuration)) : {};
        if (this.paymentModel.paymentPlatformLiveMode() || !googlePayConfiguration.merchantName) {
            googlePayConfiguration.merchantName = this.configurationModel.app()?.googlePayMerchantName;
        }

        // noinspection JSUnusedGlobalSymbols,JSUnusedLocalSymbols
        this.adyenGooglePay = new (<any>window).AdyenWeb.GooglePay(this.adyenCheckout, {
            configuration: googlePayConfiguration,
            existingPaymentMethodRequired: true,
            amount: {
                value: Math.round(amount * 100),
                currency: "AUD"
            },
            countryCode: "AU",
            environment: this.paymentModel.paymentPlatformLiveMode() ? "PRODUCTION" : "TEST",
            emailRequired: this.paymentModel.requestPayerEmail(),
            billingAddressRequired: this.paymentModel.requestPayerPhone() || this.paymentModel.requestPayerName() || this.paymentModel.requestPayerPostcode(),
            billingAddressParameters: {
                format: "MIN",
                phoneNumberRequired: this.paymentModel.requestPayerPhone()
            },
            onAuthorized: (
                paymentData: AdyenGooglePaymentData,
                actions: {
                    resolve: () => void,
                    reject: () => void
                }
            ) => {
                if (paymentData.authorizedEvent) {
                    this.submissionResult.payerEmail = paymentData.authorizedEvent.email;
                    if (paymentData.authorizedEvent.paymentMethodData && paymentData.authorizedEvent.paymentMethodData.info && paymentData.authorizedEvent.paymentMethodData.info.billingAddress) {
                        this.submissionResult.payerPhone = paymentData.authorizedEvent.paymentMethodData.info.billingAddress.phoneNumber;
                        this.submissionResult.payerName = paymentData.authorizedEvent.paymentMethodData.info.billingAddress.name;
                    }
                    if (paymentData.billingAddress) {
                        this.submissionResult.payerPostcode = paymentData.billingAddress.postalCode;
                    }
                }
                actions.resolve();
            },
            onSubmit: (
                state: {
                    data: any,
                    isValid: boolean
                },
                elementRef: any,
                actions: {
                    resolve: (resultCode: string) => void,
                    reject: () => void
                }
            ) => {
                if (state.isValid) {
                    actions.resolve("Received");
                    this.submissionResult.token = btoa(JSON.stringify(state.data));
                    void this.paymentModel.analyticsService.trackWalletPaymentRequestSucceeded(this.walletProvider());
                    this.resolveSubmission(this.submissionResult);
                } else {
                    actions.reject();
                }
            },
            onError: (
                error: {
                    name: string,
                    cause: {
                        statusCode: string
                    }
                },
                elementRef: any
            ) => {
                void this.paymentModel.analyticsService.trackWalletPaymentRequestCancelled(this.walletProvider());
                this.rejectSubmission && this.rejectSubmission(new WalletPaymentSubmissionFailure(error.name === "CANCEL" ? WalletPaymentErrorType.GOOGLE_PAY_CANCELLED : WalletPaymentErrorType.PROVIDER_ERROR,
                    undefined, error.name, error.cause.statusCode));
            }
        });
    }

    public async showPaymentSheet(amount: number): Promise<void> {
        if (this.walletProvider() == WalletProvider.APPLE_PAY) {
            this.buildApplePayComponent(amount);
            this.adyenApplePay.submit();
        } else if (this.walletProvider() == WalletProvider.GOOGLE_PAY) {
            this.buildGooglePayComponent(amount);
            this.adyenGooglePay.submit();
        }
    }
}
